import { useCallback, useState, useEffect, useMemo, useRef } from "react";
import { FormControl, InputLabel, Select, MenuItem, Checkbox, ListItemText, Grid, makeStyles, Button, Box, Accordion, AccordionSummary, AccordionDetails, Typography } from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";

const useStyles = makeStyles({
    filterFormControl: {
        minWidth: 300,
        maxWidth: 500,
    },
    buttonGroup: {
        display: 'flex',
        gap: '8px',
        padding: '8px 16px',
        position: 'sticky',
        top: 0,
        backgroundColor: '#424242',
        zIndex: 1,
        borderBottom: '1px solid #666',
    },
    accordion: {
        backgroundColor: '#303030',
        boxShadow: 'none',
        '&:before': {
            display: 'none',
        },
        '&.Mui-expanded': {
            margin: 0,
        },
    },
    accordionSummary: {
        minHeight: '48px !important',
        '&.Mui-expanded': {
            minHeight: '48px !important',
        },
        '& .MuiAccordionSummary-content': {
            margin: '12px 0 !important',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexGrow: 1,
        },
    },
    accordionDetails: {
        padding: 0,
        display: 'flex',
        flexDirection: 'column',
    },
    fieldHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexGrow: 1,
        gap: '8px',
    },
    fieldButtons: {
        display: 'flex',
        gap: '4px',
    },
    fieldLabel: {
        flexGrow: 1,
    },
});

interface FilterFieldConfig {
    fieldKey: string;
    label: string;
    items: string[];
    excludeItems: string[];
}

interface MultiSelectFilterProps {
    // Single-field mode props (for backward compatibility)
    items?: string[];
    excludeItems?: string[];
    onExcludeItemsChange?: (excludeItems: string[]) => void;

    // Multi-field mode props
    fields?: FilterFieldConfig[];
    onFiltersChange?: (filters: Record<string, string[]>) => void;

    // Shared props
    label?: string;
    /** HTML id for the label/select pair. Auto-derived from label if omitted. */
    id?: string;
    /**
     * Optional function to transform/group items for display.
     * Only applicable in single-field mode.
     */
    itemTransformer?: (items: string[]) => Set<string>;
    /**
     * Optional function to handle special logic when a transformed item is toggled.
     * Only applicable in single-field mode.
     */
    onTransformedItemToggle?: (item: string, isCurrentlySelected: boolean, allItems: string[], currentExcludeItems: string[]) => string[] | null;
    /**
     * Optional function to determine if a transformed item is currently selected.
     * Only applicable in single-field mode.
     */
    isTransformedItemSelected?: (item: string, allItems: string[], currentExcludeItems: string[]) => boolean;
}

export const MultiSelectFilter = ({
    items,
    excludeItems,
    onExcludeItemsChange,
    fields,
    onFiltersChange,
    label = "Filter Items",
    id,
    itemTransformer,
    onTransformedItemToggle,
    isTransformedItemSelected,
}: MultiSelectFilterProps) => {
    const classes = useStyles();
    const formControlRef = useRef<HTMLDivElement>(null);
    const labelId = id ?? label.toLowerCase().replace(/\s+/g, "-") + "-label";

    // Detect mode based on props
    const isMultiFieldMode = fields !== undefined && fields.length > 0;

    // Single-field mode state
    const [pendingExcludeItems, setPendingExcludeItems] = useState<string[]>(excludeItems || []);

    // Multi-field mode state
    const [pendingFilters, setPendingFilters] = useState<Record<string, string[]>>(() => {
        if (isMultiFieldMode && fields) {
            return fields.reduce((acc, field) => {
                acc[field.fieldKey] = field.excludeItems;
                return acc;
            }, {} as Record<string, string[]>);
        }
        return {};
    });

    const [isOpen, setIsOpen] = useState(false);
    const [showAllChecked, setShowAllChecked] = useState(false);

    // Sync local state when parent state changes
    useEffect(() => {
        if (isMultiFieldMode && fields) {
            setPendingFilters(fields.reduce((acc, field) => {
                acc[field.fieldKey] = field.excludeItems;
                return acc;
            }, {} as Record<string, string[]>));
        } else if (excludeItems) {
            setPendingExcludeItems(excludeItems);
        }
        setShowAllChecked(false);
    }, [excludeItems, items, fields, isMultiFieldMode]);

    // Handle clicks outside the component to close the dropdown
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (isOpen && formControlRef.current && !formControlRef.current.contains(event.target as Node)) {
                if (isMultiFieldMode && fields) {
                    setPendingFilters(fields.reduce((acc, field) => {
                        acc[field.fieldKey] = field.excludeItems;
                        return acc;
                    }, {} as Record<string, string[]>));
                } else if (excludeItems) {
                    setPendingExcludeItems(excludeItems);
                }
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen, excludeItems, fields, isMultiFieldMode]);

    // Apply transformation if provided, otherwise use raw items (single-field mode only)
    const displayItems = useMemo(() => {
        if (isMultiFieldMode || !items) return [];
        return itemTransformer ? Array.from(itemTransformer(items)) : items;
    }, [itemTransformer, items, isMultiFieldMode]);

    // Calculate checked items based on pendingExcludeItems (single-field mode only)
    const checkedItems = useMemo(() => {
        if (isMultiFieldMode) return [];

        const noneExcluded = pendingExcludeItems.length === 0;

        if (noneExcluded) {
            return showAllChecked ? displayItems : [];
        }

        return displayItems.filter(item => {
            if (isTransformedItemSelected && items) {
                return isTransformedItemSelected(item, items, pendingExcludeItems);
            }
            return !pendingExcludeItems.includes(item);
        });
    }, [pendingExcludeItems, showAllChecked, displayItems, isTransformedItemSelected, items, isMultiFieldMode]);

    const handleFilterChange = useCallback((event: any) => {
            if (isMultiFieldMode || !items) return;

            const value = event.target.value;

            setShowAllChecked(false);

            if (onTransformedItemToggle) {
                const previousChecked = new Set(checkedItems);
                const newChecked = new Set(value);

                let toggledItem: string | null = null;
                for (const item of displayItems) {
                    if (previousChecked.has(item) !== newChecked.has(item)) {
                        toggledItem = item;
                        break;
                    }
                }

                if (toggledItem) {
                    const wasChecked = previousChecked.has(toggledItem);
                    const result = onTransformedItemToggle(toggledItem, wasChecked, items, pendingExcludeItems);
                    if (result !== null) {
                        setPendingExcludeItems(result);
                        return;
                    }
                }
            }

            let newExcludeItems: string[];
            if (itemTransformer) {
                newExcludeItems = items.filter(item => {
                    const displayItem = Array.from(itemTransformer([item]))[0];
                    return !value.includes(displayItem);
                });
            } else {
                newExcludeItems = items.filter(item => !value.includes(item));
            }
            setPendingExcludeItems(newExcludeItems);
    }, [checkedItems, displayItems, onTransformedItemToggle, items, pendingExcludeItems, itemTransformer, isMultiFieldMode]);

    const handleSelectAll = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setPendingExcludeItems(() => []);
        setShowAllChecked(() => true);
    }, []);

    const handleClearAll = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (items) {
            setPendingExcludeItems(() => [...items]);
        }
        setShowAllChecked(() => false);
    }, [items]);

    // Multi-field mode handlers
    const handleFieldSelectAll = useCallback((fieldKey: string) => (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setPendingFilters(prev => ({
            ...prev,
            [fieldKey]: []
        }));
    }, []);

    const handleFieldClearAll = useCallback((fieldKey: string, fieldItems: string[]) => (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setPendingFilters(prev => ({
            ...prev,
            [fieldKey]: [...fieldItems]
        }));
    }, []);

    const handleFieldItemToggle = useCallback((fieldKey: string, item: string, fieldItems: string[]) => {
        const currentExcluded = pendingFilters[fieldKey] || [];
        const isCurrentlyExcluded = currentExcluded.includes(item);

        const newExcluded = isCurrentlyExcluded
            ? currentExcluded.filter(i => i !== item)
            : [...currentExcluded, item];

        setPendingFilters(prev => ({
            ...prev,
            [fieldKey]: newExcluded
        }));
    }, [pendingFilters]);

    const handleApply = useCallback(() => {
        if (isMultiFieldMode && onFiltersChange) {
            onFiltersChange(pendingFilters);
        } else if (onExcludeItemsChange) {
            onExcludeItemsChange(pendingExcludeItems);
        }
        setIsOpen(false);
    }, [isMultiFieldMode, onFiltersChange, pendingFilters, onExcludeItemsChange, pendingExcludeItems]);

    const handleCancel = useCallback(() => {
        if (isMultiFieldMode && fields) {
            setPendingFilters(fields.reduce((acc, field) => {
                acc[field.fieldKey] = field.excludeItems;
                return acc;
            }, {} as Record<string, string[]>));
        } else if (excludeItems) {
            setPendingExcludeItems(excludeItems);
        }
        setIsOpen(false);
    }, [isMultiFieldMode, fields, excludeItems]);

    // Compute render value display — always reflects the committed (applied) state,
    // not the pending selection, so the chip label doesn't update while the dropdown is open.
    const renderValueDisplay = useMemo(() => {
        if (isMultiFieldMode && fields) {
            const totalFiltersApplied = fields.reduce((sum, field) => {
                return sum + (field.items.length - field.excludeItems.length);
            }, 0);

            const totalPossibleFilters = fields.reduce((sum, field) => sum + field.items.length, 0);

            if (totalFiltersApplied === totalPossibleFilters) {
                return "No filters applied";
            }

            const numFieldsWithFilters = fields.filter(field => field.excludeItems.length > 0).length;

            return `${totalFiltersApplied} filter${totalFiltersApplied === 1 ? '' : 's'} applied${numFieldsWithFilters > 0 ? ` across ${numFieldsWithFilters} field${numFieldsWithFilters === 1 ? '' : 's'}` : ''}`;
        } else {
            if (!excludeItems || excludeItems.length === 0) {
                return "No filters applied";
            }
            if (!items) return "No filters applied";
            const count = items.length - excludeItems.length;
            return `${count} filter${count === 1 ? '' : 's'} applied`;
        }
    }, [isMultiFieldMode, fields, excludeItems, items]);

    // Validation
    if (isMultiFieldMode) {
        if (!fields || fields.length === 0) {
            return null;
        }
    } else {
        if (!items || items.length === 0) {
            return null;
        }
    }

    return (
        <Grid item>
            <FormControl ref={formControlRef} className={classes.filterFormControl} variant="outlined">
                    <InputLabel id={labelId}>{label}</InputLabel>
                    <Select
                        labelId={labelId}
                        multiple
                        open={isOpen}
                        onOpen={() => setIsOpen(true)}
                        onClose={(e) => {
                            if ((e.target as HTMLElement).tagName === 'BUTTON') {
                                e.preventDefault();
                                return;
                            }
                        }}
                        value={isMultiFieldMode ? [] : checkedItems}
                        onChange={isMultiFieldMode ? undefined : handleFilterChange}
                        MenuProps={{
                            anchorOrigin: {
                                vertical: 'bottom',
                                horizontal: 'left',
                            },
                            transformOrigin: {
                                vertical: 'top',
                                horizontal: 'left',
                            },
                            getContentAnchorEl: null,
                            disableAutoFocusItem: true,
                            PaperProps: {
                                style: {
                                    maxHeight: 400,
                                },
                                onMouseDown: (e) => e.stopPropagation(),
                            },
                        }}
                        renderValue={() => renderValueDisplay}
                        label={label}
                    >
                        {isMultiFieldMode ? (
                            // Multi-field mode content
                            [
                                <Box key="button-group" className={classes.buttonGroup}>
                                    <Box style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>
                                        <Button
                                            size="small"
                                            variant="contained"
                                            color="primary"
                                            onClick={handleApply}
                                            onMouseDown={(e) => e.stopPropagation()}
                                        >
                                            Apply
                                        </Button>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={handleCancel}
                                            onMouseDown={(e) => e.stopPropagation()}
                                        >
                                            Cancel
                                        </Button>
                                    </Box>
                                </Box>,
                                ...(fields?.map((field) => {
                                    const fieldExcluded = pendingFilters[field.fieldKey] || [];
                                    return (
                                        <Accordion
                                            key={field.fieldKey}
                                            className={classes.accordion}
                                            defaultExpanded
                                        >
                                            <AccordionSummary
                                                expandIcon={<ExpandMoreIcon />}
                                                className={classes.accordionSummary}
                                                onMouseDown={(e) => e.stopPropagation()}
                                            >
                                                <Box className={classes.fieldHeader}>
                                                    <Typography className={classes.fieldLabel}>
                                                        {field.label} ({field.items.length - fieldExcluded.length}/{field.items.length})
                                                    </Typography>
                                                    <Box className={classes.fieldButtons}>
                                                        <Button
                                                            size="small"
                                                            variant="text"
                                                            onClick={handleFieldClearAll(field.fieldKey, field.items)}
                                                            onMouseDown={(e) => e.stopPropagation()}
                                                        >
                                                            Clear
                                                        </Button>
                                                        <Button
                                                            size="small"
                                                            variant="text"
                                                            onClick={handleFieldSelectAll(field.fieldKey)}
                                                            onMouseDown={(e) => e.stopPropagation()}
                                                        >
                                                            All
                                                        </Button>
                                                    </Box>
                                                </Box>
                                            </AccordionSummary>
                                            <AccordionDetails className={classes.accordionDetails}>
                                                {field.items.map((item) => (
                                                    <MenuItem
                                                        key={`${field.fieldKey}-${item}`}
                                                        onClick={() => handleFieldItemToggle(field.fieldKey, item, field.items)}
                                                        onMouseDown={(e) => e.stopPropagation()}
                                                    >
                                                        <Checkbox checked={!fieldExcluded.includes(item)} />
                                                        <ListItemText primary={item} />
                                                    </MenuItem>
                                                ))}
                                            </AccordionDetails>
                                        </Accordion>
                                    );
                                }) || [])
                            ]
                        ) : (
                            // Single-field mode content (original)
                            [
                                <Box key="button-group" className={classes.buttonGroup}>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={handleClearAll}
                                        onMouseDown={(e) => e.stopPropagation()}
                                    >
                                        Clear
                                    </Button>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={handleSelectAll}
                                        onMouseDown={(e) => e.stopPropagation()}
                                    >
                                        Select All
                                    </Button>
                                    <Box style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>
                                        <Button
                                            size="small"
                                            variant="contained"
                                            color="primary"
                                            onClick={handleApply}
                                            onMouseDown={(e) => e.stopPropagation()}
                                        >
                                            Apply
                                        </Button>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={handleCancel}
                                            onMouseDown={(e) => e.stopPropagation()}
                                        >
                                            Cancel
                                        </Button>
                                    </Box>
                                </Box>,
                                ...displayItems.map((item: string) => (
                                    <MenuItem key={item} value={item}>
                                        <Checkbox checked={checkedItems.includes(item)} />
                                        <ListItemText primary={item} />
                                    </MenuItem>
                                ))
                            ]
                        )}
                    </Select>
                </FormControl>
        </Grid>
    );
};
